import { useEffect, useState } from 'react';
import { post } from '../axios/axios';

const RECENT_KEY = 'qy.package-orders.v1';
const SELECTED_KEY = 'qy.selected-package.v1';
const EVENT = 'qy-package-selection';
const read = (storage, key, fallback) => {
    try {
        return JSON.parse(storage.getItem(key)) || fallback;
    } catch {
        return fallback;
    }
};

export function getRecentPackageOrders() {
    try {
        const records = read(window.localStorage, RECENT_KEY, []);
        return Array.isArray(records)
            ? records.filter((item) => typeof item?.orderNo === 'string').slice(0, 10)
            : [];
    } catch {
        return [];
    }
}

export function rememberPackageOrder(orderNo) {
    if (!orderNo?.trim()) return;
    const entries = [
        { orderNo: orderNo.trim(), createdAt: Date.now() },
        ...getRecentPackageOrders().filter((item) => item.orderNo !== orderNo.trim()),
    ].slice(0, 10);
    try {
        window.localStorage.setItem(RECENT_KEY, JSON.stringify(entries));
    } catch {
        /* Private browsing may disable storage. */
    }
}

export function getSelectedPackageEntitlement() {
    try {
        const item = read(window.sessionStorage, SELECTED_KEY, null);
        return typeof item?.redeemCode === 'string' ? item : null;
    } catch {
        return null;
    }
}

export function selectPackageEntitlement(record) {
    if (!record?.redeemCode) throw new Error('未找到可用的套餐凭证');
    const item = {
        id: record.id,
        redeemCode: record.redeemCode,
        redeemName: record.redeemName || '已购套餐',
        // Missing types on older records must not become ordinary amount codes.
        codeType:
            typeof record.codeType === 'number' ||
            (typeof record.codeType === 'string' && record.codeType.trim() !== '')
                ? Number(record.codeType)
                : undefined,
        price: record.price,
        services: record.services || [],
        addServicesModel: record.addServicesModel || null,
    };
    window.sessionStorage.setItem(SELECTED_KEY, JSON.stringify(item));
    window.dispatchEvent(new Event(EVENT));
    return item;
}

export function clearPackageEntitlement() {
    try {
        window.sessionStorage.removeItem(SELECTED_KEY);
    } catch {
        /* Storage is optional. */
    }
    window.dispatchEvent(new Event(EVENT));
}

export function useSelectedPackageEntitlement() {
    const [selected, setSelected] = useState(getSelectedPackageEntitlement);
    useEffect(() => {
        const update = () => setSelected(getSelectedPackageEntitlement());
        window.addEventListener(EVENT, update);
        window.addEventListener('storage', update);
        return () => {
            window.removeEventListener(EVENT, update);
            window.removeEventListener('storage', update);
        };
    }, []);
    return selected;
}

export function isAmountEntitlement(record) {
    return (
        record?.codeType === 0 ||
        record?.codeType === '0' ||
        /^MLMA/.test(record?.redeemCode || '')
    );
}

export function getPackageDocType(record) {
    if (isAmountEntitlement(record)) return null;
    const name = record?.redeemName || '';
    const code = record?.redeemCode || '';
    if (/^MLM(T|S)/.test(code) || /^(任务书|调查问卷)/.test(name)) return null;
    if (/PPT/i.test(name) || (code.startsWith('MLP') && !code.startsWith('MLMP')))
        return 'DEFENSE_PPT';
    if (/降AI|降重/.test(name) || code.startsWith('R')) return 'THESIS_MODIFICATION';
    if (/开题/.test(name) || code.startsWith('MLMO')) return 'PROPOSAL_REPORT';
    if (/文献综述/.test(name) || code.startsWith('MLMLR')) return 'LITERATURE_REVIEW';
    return 'GRADUATION_THESIS';
}

export function getPackageDestination(record) {
    if (isAmountEntitlement(record)) return '/?package=selected&docType=GRADUATION_THESIS';
    if (!getPackageDocType(record))
        throw new Error('此项为独立任务书或调查问卷，请携套餐凭证联系客服使用，不会消耗权益。');
    return `/?package=selected&docType=${encodeURIComponent(getPackageDocType(record))}`;
}

export async function buildPackageShareUrl(record) {
    const response = await post('/member/redeem/code/share', { code: record.redeemCode });
    if (!response.success || !response.data?.token)
        throw new Error(response.msg || '生成领取链接失败');
    return `${window.location.origin}/package/claim#token=${encodeURIComponent(response.data.token)}`;
}

export async function resolvePackageShare(token) {
    const response = await post('/member/redeem/code/resolve', { token });
    if (!response.success || !response.data?.redeemCode)
        throw new Error(response.msg || '该领取链接已失效或套餐已使用');
    return response.data;
}
